import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wrench, Clock, Zap, Package, ShieldCheck } from "lucide-react";
import { Stars } from "@/components/KitReviews";

const SITE = "https://spa-report-hub.emergent.host";

const CATEGORY_LABELS = {
  heating: "Chauffage",
  pump: "Pompe",
  electronics: "Électronique",
  water: "Traitement eau",
  filter: "Filtration",
  other: "Autres",
};

const DIFFICULTY_STYLES = {
  easy: { label: "Facile", cls: "bg-emerald-100 text-emerald-800" },
  medium: { label: "Intermédiaire", cls: "bg-amber-100 text-amber-800" },
  hard: { label: "Expert", cls: "bg-red-100 text-red-800" },
};

export default function Kits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "";
  const activeBrand = searchParams.get("brand") || "";
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useSEO({
    title: "Kits de réparation spa — Prêts à monter | DoctoSpa",
    description:
      "Kits de réparation spa livrés à domicile : pressostat, chauffe-eau, pompes… Tout inclus avec notice pas-à-pas. Livraison France + Europe.",
    url: `${SITE}/kits`,
    type: "website",
  });

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCategory) params.category = activeCategory;
    if (activeBrand) params.brand = activeBrand;
    api
      .get("/kits", { params })
      .then((r) => setItems(r.data.items || []))
      .finally(() => setLoading(false));
  }, [activeCategory, activeBrand]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-14 lg:py-20" data-testid="kits-page">
      <div className="max-w-2xl">
        <div className="eyebrow text-secondary">Boutique</div>
        <h1 className="mt-3 font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
          Kits de réparation <span className="text-primary">prêts à monter</span>.
        </h1>
        <p className="mt-5 text-muted-foreground leading-relaxed">
          Pièces sélectionnées, calibrées, livrées ensemble. Chaque kit inclut une notice pas-à-pas
          et se monte en moins d'1h avec vos outils habituels.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-secondary" /> Garantie 2 ans</span>
          <span className="inline-flex items-center gap-1.5"><Package className="h-4 w-4 text-primary" /> Livraison 24-72h</span>
          <span className="inline-flex items-center gap-1.5"><Wrench className="h-4 w-4 text-amber-600" /> Notice fournie</span>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-10 flex flex-wrap gap-2" data-testid="kits-category-filter">
        <button
          onClick={() => setParam("category", "")}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
            !activeCategory ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
          }`}
          data-testid="kits-filter-all"
        >
          Toutes catégories
        </button>
        {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
          <button
            key={slug}
            onClick={() => setParam("category", slug)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              activeCategory === slug ? "bg-secondary text-white" : "bg-black/5 hover:bg-black/10 text-foreground"
            }`}
            data-testid={`kits-filter-${slug}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <p className="mt-16 text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card className="mt-12 p-10 border-dashed text-center" data-testid="kits-empty">
          <Package className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-display text-xl font-bold">Aucun kit disponible pour l'instant.</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            De nouveaux kits arrivent bientôt. Revenez d'ici quelques jours !
          </p>
        </Card>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kits-grid">
          {items.map((k) => {
            const diff = DIFFICULTY_STYLES[k.difficulty] || DIFFICULTY_STYLES.medium;
            return (
              <Link
                key={k.id}
                to={`/kits/${k.slug}`}
                className="group"
                data-testid={`kit-card-${k.slug}`}
              >
                <Card className="h-full border-black/10 overflow-hidden hover:border-primary/40 hover:shadow-xl transition-all flex flex-col">
                  <div className="aspect-[4/3] bg-gradient-to-br from-primary/10 via-secondary/20 to-slate-900 grid place-items-center overflow-hidden">
                    {k.cover_photo_url ? (
                      <img
                        src={`${backend}${k.cover_photo_url}`}
                        alt={k.name}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <Package className="h-16 w-16 text-white/40" strokeWidth={1.2} />
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[10px]">
                      <Badge variant="secondary" className="rounded-full">{CATEGORY_LABELS[k.category] || k.category}</Badge>
                      {k.spa_brand && <Badge variant="outline" className="rounded-full">{k.spa_brand}</Badge>}
                      <Badge className={`rounded-full border-0 ${diff.cls}`}>{diff.label}</Badge>
                    </div>
                    <h3 className="mt-3 font-display font-bold text-lg leading-tight group-hover:text-primary transition-colors line-clamp-2">
                      {k.name}
                    </h3>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed line-clamp-3 flex-1">
                      {k.short_description}
                    </p>
                    {k.reviews_count > 0 && (
                      <span className="mt-3 flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`kit-rating-${k.slug}`}>
                        <Stars value={k.rating_avg} className="h-3 w-3" /> {k.rating_avg}/5 ({k.reviews_count})
                      </span>
                    )}
                    <div className="mt-4 flex items-end justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {k.estimated_time_min} min
                      </span>
                      <span className="font-display text-2xl font-black text-primary tracking-tight">
                        {k.price_display}
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
